import React from 'react';
import { Menu, LogOut, Award, Settings, BookOpen } from 'lucide-react';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
  hideNavigation?: boolean;
}

export function Layout({ children, hideNavigation }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { company } = useCompany();
  const { user, logout } = useAuth();

  if (hideNavigation) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="min-h-screen bg-gray-50 p-4">
          {children}
        </div>
      </div>
    );
  }

  const navigation = [
    { name: 'Certificações', icon: Award, href: '/admin/certificates' },
    { name: 'Módulos', icon: BookOpen, href: '/admin/modules' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        style={{ 
          '--tw-shadow-color': company.primaryColor + '20',
        } as React.CSSProperties}
      >
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 flex items-center justify-center rounded bg-blue-100">
              <Award className="h-5 w-5 text-blue-600" />
            </div>
            <span className="font-medium text-gray-900">
              Administração
            </span>
          </div>
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-medium text-gray-900">{user?.name}</p>
          <p className="text-xs text-gray-500">{user?.email}</p>
        </div>

        <nav className="mt-4">
          {navigation.map((item) => {
            const isActive = window.location.pathname === item.href;
            return (
              <a
                key={item.name}
                href={item.href}
                className={`flex items-center px-4 py-3 text-gray-600 transition-colors hover:text-gray-900 ${
                  isActive ? 'bg-blue-50 text-blue-600' : ''
                }`}
                style={{
                  backgroundColor: isActive ? company.primaryColor + '10' : undefined,
                  color: isActive ? company.primaryColor : undefined,
                  '&:hover': {
                    backgroundColor: company.primaryColor + '10',
                    color: company.primaryColor,
                  },
                } as React.CSSProperties}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
              </a>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-full p-4">
          <button 
            onClick={logout}
            className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 transition-colors"
            style={{
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              '&:hover': {
                backgroundColor: '#FEE2E2',
              },
            } as React.CSSProperties}
          >
            <LogOut className="h-5 w-5" />
            Sair
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <div className="min-h-screen bg-gray-50 p-4">
          {children}
        </div>
      </div>
    </div>
  );
}